import { getAddress } from 'viem';
import Image from 'next/image';
import Link from 'next/link';
import { useRouter } from 'next/router';
import React, { useContext, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useAccount, useWalletClient } from 'wagmi';
import { getContract } from 'wagmi/actions';
import CollectionTabs from '../../../components/collectrions/Collection_items';
import Icon, { ChainIcon } from '../../../components/Icon';
import Meta from '../../../components/Meta';
import { shortenAddress } from '../../../helpers/address';
import { normalizeEther } from '../../../helpers/digits';
import { filterServerSideProps, ServerPropsContext } from '../../../helpers/filterServerSideProps';
import treasuryABI from '../../../helpers/treasuryABI';
import useGetUser from '../../../hooks/useGetUser';
import { getCdnUrl } from '../../../lib/cdnUrl';
import { setModal } from '../../../redux/counterSlice';
import useGetCollections from '../../../hooks/useGetCollections';
import Feature_collections_carousel from '../../../components/carousel/Feature_collection_carousel';
import CollectionBadges from '../../../components/collectrions/collection_badges';
import CollectionMetrics from '../../../components/collectrions/collection_metrics';
import getChainName from '../../../helpers/getChainName';
import { ImageFixedAO } from '../../../components/Image_fixed_aspect_ratio';
import Socials from '../../../components/Socials';
import { useConnectModal } from '@rainbow-me/rainbowkit';
import { DataRefreshContext } from '../../../components/refreshContext';
import { getSupportedChain } from '../../../helpers/getSupportedChain';
import CollectionDescription from '../../../components/collectrions/collection_description';
import { UWImportErc20Buttons } from '../../../components/universalWrapper/UWImportErc20Buttons';
import { WrappedCollectionsSvg } from '../../../components/WrappedCollectionsSvg';

export const getServerSideProps = filterServerSideProps([
  'collectionBySlug',
  'globalBidsBySlug',
  'globalLendOffersBySlug',
]);

const CollectionActionButton = ({ onClick, children }) => {
  return (
    <>
      <div className='flex flex-row items-center capitalize'>
        <div className='rounded-xl border border-none bg-accent text-white hover:bg-accent-light'>
          <div
            className='js-likes relative inline-flex cursor-pointer items-center justify-center p-2 text-sm'
            onClick={onClick}
            data-testid='collectionActionButtons'
          >
            {children}
          </div>
        </div>
      </div>
    </>
  );
};
let firstRender = true;
const Collection = ({ serverProps }) => {
  const [serverPropsState, setServerPropsState] = useState(serverProps);
  const collection = serverPropsState.collection;

  const [treasuryValue, setTreasuryValue] = useState(0);
  const dispatch = useDispatch();
  const router = useRouter();
  const { openConnectModal } = useConnectModal();
  const { isConnected } = useAccount();
  const pid = collection?.address;
  const collectionMetric = collection?.collectionmetrics || null;
  const { data: swr } = useGetCollections({
    filter: { parentCollectionAddress: pid, includeChildCollections: true },
    limit: 50,
    page: 1,
  });
  const { results: data } = swr || {};
  const { refreshDate } = useContext(DataRefreshContext);
  const { address } = useAccount();
  const currentChain = getSupportedChain(collection?.chain);

  useEffect(() => {
    if (!pid) router.push('/400');
    if (!collection?.isActive) {
      router.push('/400');
    }
  }, [serverPropsState]);

  useEffect(() => {
    if (firstRender) {
      firstRender = false;
      return;
    }
    (async () => {
      const data = await filterServerSideProps([
        'collectionBySlug',
        'globalBidsBySlug',
        'globalLendOffers',
      ])({
        params: {
          chain: collection?.chain,
          collection: collection?.address,
        },
      });
      if (data?.props?.serverProps) setServerPropsState(data?.props?.serverProps);
    })();
  }, [refreshDate, pid]);

  const activities = serverPropsState.activities;

  const globalBids = serverPropsState?.globalBids;
  const globalLendOffers = serverPropsState?.globalLendOffers;

  const creator = useGetUser(collection?.creatorAddress).data;

  const signer = useWalletClient();

  const treasuryContract = getContract({
    address: currentChain?.treasuryAddress,
    abi: treasuryABI,
    chainId: Number(currentChain?.id),
    walletClient: signer.data,
  });

  useEffect(() => {
    if (!treasuryContract) {
      return;
    }

    const totalValue = async () => {
      if (!pid) return;

      const tAddress = getAddress(pid);

      const tValue = await treasuryContract.read.collectionBalances([tAddress]);

      const teValue = normalizeEther(tValue, 18);

      setTreasuryValue(teValue);
    };

    return () => totalValue();
  }, [treasuryContract, pid]);

  const ActionButtons = () => {
    return (
      <>
        {collection?.contractType === 'ERC721' &&
          (!collection?.mirrorAddress ? (
            <CollectionActionButton
              onClick={() => {
                router.push({
                  pathname: '/wrapping-form',
                  query: {
                    collectionAddress: collection?.address,
                    name: collection?.name,
                    description: collection?.description,
                    chain: collection?.chain,
                  },
                });
              }}
              className='flex flex-row items-center capitalize'
            >
              <span className='flex'>Wrap</span>
            </CollectionActionButton>
          ) : (
            <CollectionActionButton>
              <Link
                href={`/collection/${collection?.chain}/${
                  collection?.pathName
                }_wrapped${collection?.mirrorAddress?.slice(-5)}`}
              >
                <a className='flex flex-row items-center gap-1'>
                  <WrappedCollectionsSvg className='h-4 w-4' />
                  <span className='flex'>Go to Wrapped Collection</span>
                </a>
              </Link>
            </CollectionActionButton>
          ))}

        {collection?.isMinting && (
          <Link
            href={'/nft/minting/' + collection?.address}
            className='flex flex-row items-center capitalize'
          >
            <a
              target='_blank'
              className='rounded-xl border border-none bg-accent text-white hover:bg-accent-light'
            >
              <div
                className='js-likes relative inline-flex cursor-pointer items-center justify-center p-2 text-sm'
                data-testid='collectionActionButtons'
              >
                Mint Now
              </div>
            </a>
          </Link>
        )}
        <CollectionActionButton
          onClick={() => {
            if (!isConnected) {
              return openConnectModal();
            }
            dispatch(
              setModal({
                key: 'giveGlobalBidModal',
                data: pid,
              }),
            );
          }}
        >
          <Icon name={'icon-bid'} fillCurrent />
          <span className='flex'>
            <span className='mr-1 hidden lg:block'>Make a</span> global bid
          </span>
        </CollectionActionButton>
        {collection?.traitBidsEnabled && (
          <CollectionActionButton
            onClick={() => {
              if (!isConnected) {
                return openConnectModal();
              }
              dispatch(
                setModal({
                  key: 'giveGlobalBidModal',
                  data: pid,
                  traits: true,
                }),
              );
            }}
          >
            <Icon name={'icon-bid'} fillCurrent />
            <span className='flex'>
              <span className='mr-1 hidden lg:block'>Make a</span> global trait bid
            </span>
          </CollectionActionButton>
        )}
        {collection?.isVerified && (
          <CollectionActionButton
            onClick={() => {
              if (!isConnected) {
                return openConnectModal();
              }
              dispatch(
                setModal({
                  key: 'lendOfferModal',
                  data: {
                    collection,
                  },
                }),
              );
            }}
          >
            <Icon name={'icon-bid'} fillCurrent />
            <span>Global Lend Offer</span>
          </CollectionActionButton>
        )}
        {collection?.rarityBidsEnabled && (
          <CollectionActionButton
            onClick={() => {
              if (!isConnected) {
                return openConnectModal();
              }
              dispatch(
                setModal({
                  key: 'giveGlobalBidModal',
                  data: pid,
                  rarity: true,
                }),
              );
            }}
          >
            <Icon name={'icon-bid'} fillCurrent />
            <span className='flex'>
              <span className='mr-1 hidden lg:block'>Make a</span> global rarity bid
            </span>
          </CollectionActionButton>
        )}
      </>
    );
  };

  return (
    <ServerPropsContext.Provider value={serverPropsState}>
      <Meta
        title={`${
          collection?.name ? collection?.name + ' || ' : ''
        }Bit5 | Create. Collect. Connect.`}
      />

      <div className={[!serverPropsState?.collection ? 'hidden' : ''].join(' ')}>
        <div className='relative h-full max-h-[360px] min-h-[200px] pb-[20%]'>
          <Image
            src={
              collection?.coverPhotoPath
                ? getCdnUrl(collection?.coverPhotoPath, 'w200', collection?.updatedAt)
                : '/images/collections/_Bit5_U_Cover_1400x500.png'
            }
            alt='banner'
            layout='fill'
            objectFit='cover'
          />

          <div className='m-4 flex justify-end'>
            <Socials data={collection} isCollection />
          </div>
        </div>
        <div
          className={[
            'flex w-full flex-col',
            'bg-gradient-to-t px-[36px] pt-[24px]',
            'dark:from-[#0e102dff] dark:via-[#0e102dff] dark:to-[#0e102d00]',
            'from-[#ffffffff] via-[#ffffffff] to-[#ffffff00]',
            'relative -top-[110px] -mb-[110px]',
          ].join(' ')}
        >
          <div className='flex w-full flex-row justify-between pb-[10px]'>
            <div className='flex w-[200px] flex-row md:w-[340px] lg:w-[480px]'>
              <div className='w-[140px] p-[2px] md:w-[220px]'>
                <ImageFixedAO
                  image={getCdnUrl(collection?.profilePhotoPath, 'detail', collection?.updatedAt)}
                  bottomRounded={true}
                  resolution={160}
                  alt={collection?.owner}
                  layout='fill'
                  objectFit='contain'
                  className='rounded-xl !border-[5px] !border-solid !border-white bg-white dark:border-jacarta-600'
                />
              </div>
              <div className='relative -left-4 mt-2'>
                <CollectionBadges
                  isDNFT={collection?.isDNFT}
                  isVerified={collection?.isVerified}
                  collectionAddress={collection?.address}
                  placement={'right'}
                  vertical
                />
              </div>
              <div className='ml-[10px] mb-[10px] hidden h-full w-full flex-col justify-end gap-[4px] md:flex'>
                <div className='flex items-center space-x-2'>
                  <p className='text-sm font-bold text-jacarta-500 dark:text-jacarta-100'>
                    Chain:{' '}
                  </p>
                  <ChainIcon
                    name={collection?.chain}
                    tooltip={getChainName(collection?.chain)}
                    type={'chain'}
                  />
                </div>
                <div className='flex'>
                  <span className='text-sm font-bold text-jacarta-500 dark:text-jacarta-100'>
                    Creator Fee:{' '}
                  </span>
                  <span className='text-sm font-bold'>{collection?.totalCreatorRoyalty}%</span>
                </div>
                <div className='flex'>
                  <span className='mr-1 text-sm font-bold text-jacarta-500 dark:text-jacarta-100'>
                    Contract:{' '}
                  </span>
                  <Link
                    href={`${getSupportedChain(collection?.chain)?.scan_url}/address/${
                      collection?.address
                    }`}
                  >
                    <a className='flex items-center text-sm font-bold text-accent' target='_blank'>
                      <span className='mr-1.5 text-sm font-bold'>
                        {shortenAddress(collection?.address ?? '')}
                      </span>
                      <span className='text-[#000] dark:text-white'>
                        <Icon type={'scan'} fillCurrent name={'scan-global'} />
                      </span>
                    </a>
                  </Link>
                </div>
                <div className='flex'>
                  <span className='pr-[4px] text-sm font-bold text-jacarta-500 dark:text-jacarta-100'>
                    Created by:
                  </span>
                  <Link href={'/user/' + collection?.creatorAddress ?? ''}>
                    <a className='text-sm font-bold text-accent'>
                      <span className='text-sm font-bold'>
                        {creator?.isRegistered
                          ? creator?.info.name ?? ''
                          : shortenAddress(collection?.creatorAddress ?? '')}
                      </span>
                    </a>
                  </Link>
                </div>
                {collection?.mirrorAddress && collection?.contractType === 'DN404' && (
                  <div className='flex flex-row items-center'>
                    <span className='pr-[4px] text-sm font-bold text-jacarta-500 dark:text-jacarta-100'>
                      Import ERC20:
                    </span>
                    <UWImportErc20Buttons collection={collection} />
                  </div>
                )}

                {/* Edit Collection */}
                <div className='flex'>
                  {address && creator?.walletAddress && creator?.walletAddress === address ? (
                    <div className='mb-4 text-base font-bold text-accent'>
                      <Link href={`/collection/edit/${collection?.address}`}>
                        <a>Edit Collection</a>
                      </Link>
                    </div>
                  ) : null}
                </div>
              </div>
            </div>

            <div className='hidden flex-col justify-end gap-1 sm:flex'>
              <div className='flex w-full flex-row flex-wrap justify-end gap-1'>
                <ActionButtons />
              </div>
              <div className='mt-2 hidden justify-end md:flex'>
                <CollectionMetrics
                  collectionMetric={collectionMetric}
                  treasuryValue={treasuryValue}
                />
              </div>
            </div>
          </div>

          <div className='flex sm:hidden'>
            <div className='flex w-full flex-col justify-center gap-1'>
              <div className='flex w-full flex-row flex-wrap gap-1'>
                <ActionButtons />
              </div>
            </div>
          </div>

          <div className='my-2 flex w-full overflow-auto md:hidden'>
            <CollectionMetrics collectionMetric={collectionMetric} treasuryValue={treasuryValue} />
          </div>

          <div className='my-[10px] flex h-full w-full flex-col justify-between gap-3 overflow-auto px-[4px] md:hidden'>
            <div className='flex min-w-[330px] flex-row justify-between'>
              <div className='flex items-center space-x-2'>
                <p className='text-sm font-bold text-jacarta-500 dark:text-jacarta-100'>Chain: </p>
                <ChainIcon
                  name={collection?.chain}
                  tooltip={getChainName(collection?.chain)}
                  type={'chain'}
                />
              </div>
              <div className='flex'>
                <span className='text-sm font-bold text-jacarta-500 dark:text-jacarta-100'>
                  Creator Fee:{' '}
                </span>
                <span className='text-sm font-bold'>{collection?.totalCreatorRoyalty}%</span>
              </div>
            </div>

            <div className='flex min-w-[330px] flex-row justify-between'>
              <div className='flex'>
                <span className='mr-1 text-sm font-bold text-jacarta-500 dark:text-jacarta-100'>
                  Contract:{' '}
                </span>
                <Link
                  href={`${getSupportedChain(collection?.chain)?.scan_url}/address/${
                    collection?.address
                  }`}
                >
                  <a className='flex items-center text-sm font-bold text-accent' target='_blank'>
                    <span className='mr-1.5 text-sm font-bold'>
                      {shortenAddress(collection?.address ?? '')}
                    </span>
                    <span className='text-[#000] dark:text-white'>
                      <Icon type={'scan'} fillCurrent name={'scan-global'} />
                    </span>
                  </a>
                </Link>
              </div>
              <div className='flex'>
                <span className='pr-[4px] text-sm font-bold text-jacarta-500 dark:text-jacarta-100'>
                  Created:
                </span>
                <Link href={'/user/' + collection?.creatorAddress ?? ''}>
                  <a className='text-sm font-bold text-accent'>
                    <span className='text-sm font-bold'>
                      {creator?.isRegistered
                        ? creator?.info.name ?? ''
                        : shortenAddress(collection?.creatorAddress ?? '')}
                    </span>
                  </a>
                </Link>
              </div>
            </div>

            {/* Edit Collection */}
            <div className='flex'>
              {address && creator?.walletAddress && creator?.walletAddress === address ? (
                <div className='mb-4 text-base font-bold text-accent'>
                  <Link href={`/collection/edit/${collection?.address}`}>
                    <a>Edit Collection</a>
                  </Link>
                </div>
              ) : null}
            </div>
          </div>

          <div className='flex w-full flex-col items-center justify-between border-t-2 border-jacarta-600 p-[4px] align-bottom sm:flex-row sm:items-start'>
            <div className='flex sm:w-5/12'>
              <h2 className='mb-2 font-display text-lg font-medium text-jacarta-700 dark:text-white md:text-2xl lg:text-2xl'>
                {collection?.name}
              </h2>
            </div>
            <div className='flex sm:w-6/12'>
              <CollectionDescription collection={collection} />
            </div>
          </div>

          {/* {treasuryValue != 0 && (
              <div className='alert-info mx-auto mt-0 mb-4 hidden w-fit rounded-lg py-2 px-4 md:block'>
                🎉 This collection has a treasury value. This means, in every purchase process,
                we'll send some fees to that treasury.
              </div>
            )} */}
        </div>
      </div>

      <div className='flex'>
        {treasuryValue !== 0 && (
          <div className='alert-info mx-auto my-4 mt-0 w-fit rounded-lg py-2 px-4'>
            🎉 This collection has a treasury value. This means, in every purchase process, we'll
            send some fees to that treasury.
          </div>
        )}
      </div>

      {data?.length > 0 && (
        <div className={'container mt-8 select-none'}>
          <h5 className={'mb-0 text-center font-display text-3xl text-jacarta-700 dark:text-white'}>
            Sub Collections
          </h5>
          <div className='relative'>
            <Feature_collections_carousel collections={data} twiceItems />
          </div>
        </div>
      )}

      <div className='mt-8'>
        <CollectionTabs
          collection={collection}
          activities={activities}
          globalBids={globalBids}
          globalLendOffers={globalLendOffers}
        />
      </div>
    </ServerPropsContext.Provider>
  );
};

export default Collection;
